import { FormEvent } from 'react';

import { BrandHeader } from '../../components/BrandHeader';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { DesktopOnly, MobileOnly } from '../../components/Layout';
import { SideImage } from '../../components/SideImage';
import { TextButton } from '../../components/TextButton';
import { SplitLayout } from '../../composites/SplitLayout';
import { layout } from '../../theme/tokens';
import {
  BackRow,
  ContentColumn,
  Form,
  FormArea,
  FormError,
  Heading,
  HeadingAccent,
  HeadingBlock,
  Panel,
  SuccessMessage,
} from './ForgottenPasswordTemplate.styles';
import type {
  ForgottenPasswordTemplateLabels,
  ForgottenPasswordTemplateProps,
} from './ForgottenPasswordTemplate.types';

const DEFAULT_LABELS: Required<ForgottenPasswordTemplateLabels> = {
  title: 'Reset your password.',
  subtitle: 'Enter your email and we will send you a reset link.',
  emailLabel: 'Email',
  submit: 'Send',
  backToSignIn: 'Back to sign in',
};

export function ForgottenPasswordTemplate({
  propertyName,
  propertyLogoUrl,
  heroImageUrl,
  imageAlt = '',
  email,
  onEmailChange,
  onSubmit,
  isSubmitting = false,
  error,
  onBackToSignIn,
  successMessage,
  labels,
}: ForgottenPasswordTemplateProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };
  const brandHeader = <BrandHeader propertyName={propertyName} logoUrl={propertyLogoUrl} />;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    onSubmit();
  };

  return (
    <>
      <MobileOnly>{brandHeader}</MobileOnly>
      <SplitLayout
        leftWidth="45%"
        height="100vh"
        left={
          <SideImage
            src={heroImageUrl}
            alt={imageAlt}
            sticky
            mobileHeight={layout.heroImageMobileHeight}
          />
        }
        right={
          <ContentColumn>
            <DesktopOnly>{brandHeader}</DesktopOnly>
            <FormArea>
              {successMessage ? (
                <Panel>
                  <SuccessMessage role="status">{successMessage}</SuccessMessage>
                  {onBackToSignIn && (
                    <BackRow>
                      <TextButton type="button" onClick={onBackToSignIn}>
                        {merged.backToSignIn}
                      </TextButton>
                    </BackRow>
                  )}
                </Panel>
              ) : (
                <Panel>
                  <HeadingBlock>
                    <Heading>
                      {merged.title} <HeadingAccent>{merged.subtitle}</HeadingAccent>
                    </Heading>
                  </HeadingBlock>

                  <Form onSubmit={handleSubmit} noValidate>
                    <Input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => onEmailChange(e.target.value)}
                      error={Boolean(error)}
                      aria-label={merged.emailLabel}
                      placeholder={merged.emailLabel}
                    />

                    {error && <FormError role="alert">{error}</FormError>}

                    <Button type="submit" variant="primary" fullWidth disabled={isSubmitting}>
                      {merged.submit}
                    </Button>
                  </Form>

                  {onBackToSignIn && (
                    <BackRow>
                      <TextButton type="button" onClick={onBackToSignIn}>
                        {merged.backToSignIn}
                      </TextButton>
                    </BackRow>
                  )}
                </Panel>
              )}
            </FormArea>
          </ContentColumn>
        }
      />
    </>
  );
}
